// Product catalog. `photo` = Unsplash stock (loads for public visitors);
// `fallback` = local SVG illustration used if the photo fails to load.
const unsplash = (id) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=640&h=640&q=70`;

// ---------------------------------------------------------------------------
// Pack sizes (cold pressed oils)
//
// Priced off the 500 ml rate so the headline price on every oil is unchanged,
// with the usual ladder: smaller packs cost more per litre, bigger packs less.
// Multipliers are applied to the 500 ml price and rounded to the nearest ₹5.
//   250 ml  0.57x  (~14% per-litre premium — trial size)
//   500 ml  1.00x  (the anchor)
//   1 L     1.87x  (~7% per-litre saving)
//   5 L     8.50x  (~15% per-litre saving)
// `label` is what the size selector shows; `unit` is what goes on the order,
// the invoice and the confirmation email.
const PACK_LADDER = [
  { id: "250ml", label: "250 ml", ml: 250, factor: 0.57 },
  { id: "500ml", label: "500 ml", ml: 500, factor: 1, default: true },
  { id: "1l", label: "1 L", ml: 1000, factor: 1.87 },
  { id: "5l", label: "5 L", ml: 5000, factor: 8.5 },
];

const roundTo5 = (n) => Math.round(n / 5) * 5;

// ---------------------------------------------------------------------------
// ₹9 launch plan (temporary — was ₹99; dropped for a limited-time promo)
//
// The pre-launch membership sold on the site: pay ₹9 now, get 12 free eggs
// with the first delivery and free delivery for the first 30 days (min. one
// dozen per delivery). It rides the normal cart → checkout → Razorpay flow as
// a regular catalogue item, so orders, emails and admin all just work.
// The id below keeps its original "-99" suffix even at the ₹9 price — it's an
// opaque identifier already referenced by past orders/carts, not the price.
export const LAUNCH_PLAN_ID = "launch-plan-99";

// Delivery pricing: ₹20 under ₹120, free from ₹120 up. Carts holding the
// founding-member plan are always free — its pitch IS free delivery. An
// already-active founding member (from a PAST paid order, within their
// 30-day window) also always rides free, regardless of what's in this cart —
// `memberFreeDelivery` carries that in from useAuth().membership.active.
// Shared by Cart (nudge) and Checkout (the actual charge) so they can't drift.
export const FREE_DELIVERY_MIN = 120;
export const DELIVERY_FEE = 20;
export const MEMBERSHIP_DAYS = 30;
export function deliveryFeeFor(lines, { memberFreeDelivery = false } = {}) {
  if (memberFreeDelivery) return 0;
  const subtotal = lines.reduce((a, l) => a + l.lineTotal, 0);
  const hasMembership = lines.some((l) => (l.product?.id || l.id) === LAUNCH_PLAN_ID);
  return hasMembership || subtotal >= FREE_DELIVERY_MIN ? 0 : DELIVERY_FEE;
}
// Launch morning in IST — the home page countdown and all "deliveries start"
// copy key off these two values.
export const LAUNCH_DATE = new Date("2026-09-01T06:00:00+05:30");
export const LAUNCH_DATE_LABEL = "1 September";

// Build the four pack variants for an oil from its 500 ml price. Each size has
// its own artwork (bottles for 250 ml–1 L, a jerrycan for 5 L), generated by
// tools/product-images.mjs. `baseMrp500ml` is the market/list price the strike
// -through on the product page compares against, scaled with the same ladder.
function packSizes(oilId, basePrice500ml, baseMrp500ml) {
  return PACK_LADDER.map((p) => ({
    id: p.id,
    label: p.label,
    ml: p.ml,
    unit: `${p.label} pack`,
    price: roundTo5(basePrice500ml * p.factor),
    mrp: baseMrp500ml ? roundTo5(baseMrp500ml * p.factor) : undefined,
    image: `/assets/products/oil-${oilId}-${p.id}.svg`,
    default: Boolean(p.default),
  }));
}

// Mirror the default variant onto the product so everything that predates
// variants — price sorting, the home page hero, the SEO feed — keeps working
// against `pricePerKg` / `unit` without special-casing.
function withDefaultVariant(product) {
  if (!product.variants?.length) return product;
  const fallbackVariant = product.variants.find((v) => v.default) || product.variants[0];
  return {
    ...product,
    pricePerKg: product.pricePerKg ?? fallbackVariant.price,
    mrp: product.mrp ?? fallbackVariant.mrp,
    unit: product.unit ?? fallbackVariant.unit,
  };
}

export const PRODUCTS = [
  {
    id: "banganapalli",
    name: "Banganapalli Mango",
    tag: "GI Tagged · Seasonal",
    desc: "The king of Andhra mangoes. Large, fibreless, golden-yellow flesh with rich sweetness.",
    pricePerKg: 120,
    mrp: 180,
    category: "mangoes",
    unit: "kg",
    badge: "Bestseller",
    soldOut: true, // mango season ended (Apr–Jun) — flip off when stock returns
    photo: unsplash("1605027990121-cbae9e0642df"),
    fallback: "/assets/products/banganapalli.svg",
    specs: [
      { label: "Variety", value: "Banganapalli (Benishan) — GI tagged" },
      { label: "Origin", value: "Rimmon Farm orchards, Krishna district, Andhra Pradesh" },
      { label: "Season", value: "April – June" },
      { label: "Fruit size", value: "300–450 g per fruit, 2–3 fruits per kg" },
      { label: "Ripening", value: "Naturally ripened — no carbide, no chemicals" },
      { label: "Taste & texture", value: "Fibreless golden flesh, rich honey-like sweetness" },
      { label: "Storage", value: "Room temperature until ripe; refrigerate ripe fruit up to 4 days" },
    ],
  },
  {
    id: "totapuri",
    name: "Totapuri Mango",
    tag: "Versatile · Tangy-Sweet",
    desc: "Distinct beak shape. Perfect for juices, pickles, and everyday eating.",
    pricePerKg: 80,
    mrp: 120,
    category: "mangoes",
    unit: "kg",
    soldOut: true,
    photo: unsplash("1553279768-865429fa0078"),
    fallback: "/assets/products/totapuri.svg",
    specs: [
      { label: "Variety", value: "Totapuri (Bangalora)" },
      { label: "Origin", value: "Rimmon Farm orchards, Chittoor district, Andhra Pradesh" },
      { label: "Season", value: "April – July" },
      { label: "Fruit size", value: "250–400 g per fruit, distinct parrot-beak tip" },
      { label: "Ripening", value: "Naturally ripened — no carbide, no chemicals" },
      { label: "Best for", value: "Juices, pickles, salads and everyday eating" },
      { label: "Storage", value: "Room temperature until ripe; refrigerate ripe fruit up to 5 days" },
    ],
  },
  {
    id: "neelam",
    name: "Neelam Mango",
    tag: "Aromatic · Late Season",
    desc: "Deep orange flesh, strong fragrance, and a balanced sweet-tart profile.",
    pricePerKg: 100,
    mrp: 150,
    category: "mangoes",
    unit: "kg",
    soldOut: true,
    photo: unsplash("1601493700631-2b16ec4b4716"),
    fallback: "/assets/products/neelam.svg",
    specs: [
      { label: "Variety", value: "Neelam" },
      { label: "Origin", value: "Rimmon Farm orchards, Chittoor district, Andhra Pradesh" },
      { label: "Season", value: "May – August (late season)" },
      { label: "Fruit size", value: "200–300 g per fruit, 3–5 fruits per kg" },
      { label: "Ripening", value: "Naturally ripened — no carbide, no chemicals" },
      { label: "Taste & texture", value: "Deep orange flesh, strong aroma, sweet-tart balance" },
      { label: "Storage", value: "Room temperature until ripe; refrigerate ripe fruit up to 5 days" },
    ],
  },
  {
    id: "eggs-free-range",
    name: "Free Range Eggs",
    tag: "Pasture-raised · Daily fresh",
    desc: "Hens raised on open pasture with natural feed. Rich yolks, collected fresh every morning.",
    pricePerKg: 90,
    mrp: 120,
    category: "eggs",
    subCategory: "free-range",
    unit: "dozen",
    badge: "Popular",
    photo: unsplash("1582722872445-44dc5f7e3c8f"),
    fallback: "/assets/products/eggs-free-range.svg",
    specs: [
      { label: "Pack size", value: "12 eggs per dozen" },
      { label: "Rearing", value: "Open-pasture hens on natural feed — no cages, no antibiotics" },
      { label: "Egg weight", value: "50–60 g per egg (medium–large)" },
      { label: "Freshness", value: "Collected every morning, delivered same day" },
      { label: "Yolk", value: "Deep golden yolks from pasture foraging" },
      { label: "Storage", value: "Refrigerate; best within 2 weeks of delivery" },
    ],
  },
  {
    id: "eggs-pasteurized",
    name: "Pasteurized Eggs",
    tag: "Safe · Heat-treated",
    desc: "Farm-fresh eggs gently pasteurized for safer use in baking, sauces, and family meals.",
    pricePerKg: 75,
    mrp: 100,
    category: "eggs",
    subCategory: "conventional",
    unit: "dozen",
    photo: unsplash("1518569656558-1f25e69d93d7"),
    fallback: "/assets/products/eggs-pasteurized.svg",
    specs: [
      { label: "Pack size", value: "12 eggs per dozen" },
      { label: "Processing", value: "Gently heat-treated in-shell to eliminate salmonella risk" },
      { label: "Egg weight", value: "50–60 g per egg (medium–large)" },
      { label: "Best for", value: "Baking, sauces, mayonnaise and meals for kids & elders" },
      { label: "Freshness", value: "Pasteurized within a day of collection" },
      { label: "Storage", value: "Refrigerate; best within 3 weeks of delivery" },
    ],
  },
  {
    id: LAUNCH_PLAN_ID,
    name: "₹9 Launch Plan — Egg Club",
    tag: "Founding member · Deliveries from 1 Sep",
    desc: "Join before launch for ₹9: 12 free pasture eggs with your first delivery, free home delivery for your first 30 days, and founding-member priority on our routes. Limited memberships only.",
    pricePerKg: 9,
    mrp: 249,
    category: "eggs",
    unit: "membership",
    badge: "Launch offer",
    // Sold only through the join CTAs (welcome popup, launch band) — never
    // browsed to or added from the catalog, so it stays out of every product
    // listing while still resolving fine via findProduct() for cart/checkout.
    unlisted: true,
    photo: unsplash("1582722872445-44dc5f7e3c8f"),
    fallback: "/assets/products/eggs-free-range.svg",
    specs: [
      { label: "Welcome gift", value: "12 pasture-raised eggs FREE with your first delivery" },
      { label: "Free delivery", value: "Every delivery free for your first 30 days from launch" },
      { label: "Minimum order", value: "Just 12 eggs (1 dozen) per delivery" },
      { label: "Deliveries start", value: "1 September — we confirm your delivery day on WhatsApp" },
      { label: "How it works", value: "Join now, tell us your address & preferred time, and your route is reserved before launch" },
      { label: "Our promise", value: "100% refund if we can't deliver to your address — no questions asked" },
      { label: "Availability", value: "Limited launch memberships · one per household" },
    ],
  },
  {
    id: "milk-fresh",
    name: "Fresh Farm Milk",
    tag: "Unhomogenized · Daily",
    desc: "Pure cow milk from our dairy, chilled and delivered within hours of milking.",
    pricePerKg: 60,
    mrp: 75,
    category: "milk",
    unit: "litre",
    badge: "Daily fresh",
    photo: unsplash("1550583724-b2692b85b150"),
    fallback: "/assets/products/milk-fresh.svg",
    specs: [
      { label: "Source", value: "Rimmon Farm dairy herd, milked twice daily" },
      { label: "Processing", value: "Unhomogenized, chilled immediately — no powders, no preservatives" },
      { label: "Fat content", value: "Full cream, approx. 4–4.5% fat" },
      { label: "Delivery", value: "Delivered within hours of milking" },
      { label: "Usage", value: "Boil before drinking; cream rises naturally on top" },
      { label: "Storage", value: "Refrigerate at 4°C; consume within 24–48 hours" },
    ],
  },
  {
    id: "milk-a2",
    name: "A2 Cow Milk",
    tag: "A2 protein · Premium",
    desc: "Desi-breed A2 milk — creamy, naturally sweet, and easier on digestion.",
    pricePerKg: 85,
    mrp: 110,
    category: "milk",
    unit: "litre",
    photo: unsplash("1563636619-e9143da7973b"),
    fallback: "/assets/products/milk-a2.svg",
    specs: [
      { label: "Source", value: "Indigenous desi-breed cows (A2 beta-casein protein)" },
      { label: "Processing", value: "Unhomogenized, chilled immediately — no powders, no preservatives" },
      { label: "Fat content", value: "Full cream, approx. 4.5–5% fat" },
      { label: "Why A2", value: "A2 protein is gentler on digestion than regular A1 milk" },
      { label: "Delivery", value: "Delivered within hours of milking" },
      { label: "Storage", value: "Refrigerate at 4°C; consume within 24–48 hours" },
    ],
  },
  {
    id: "chicken-country",
    name: "Live Country Chicken",
    tag: "Free-range · 1.2–1.5 kg",
    desc: "Healthy country birds raised on the farm. Sold live — cleaned and cut on request.",
    pricePerKg: 320,
    mrp: 400,
    category: "poultry",
    unit: "bird",
    badge: "Farm raised",
    photo: unsplash("1548550023-2bdb3c5beed7"),
    fallback: "/assets/products/chicken-country.jpg",
    images: [unsplash("1612170153139-6f881ff067e0"), "/assets/products/chicken-broiler.jpg"],
    specs: [
      { label: "Breed", value: "Native country chicken (Natu Kodi)" },
      { label: "Rearing", value: "Free-range on the farm — natural feed, no growth hormones" },
      { label: "Live weight", value: "1.2–1.5 kg per bird" },
      { label: "Age", value: "4–6 months, naturally grown" },
      { label: "Preparation", value: "Sold live; cleaned and cut on request at no extra cost" },
      { label: "Best for", value: "Country-style curries, soups and slow-cooked dishes" },
    ],
  },
  {
    id: "chicken-broiler",
    name: "Live Broiler Chicken",
    tag: "Tender · Ready to cook",
    desc: "Farm-raised broiler chickens sold live. Ideal for everyday meals and bulk orders.",
    pricePerKg: 220,
    mrp: 280,
    category: "poultry",
    unit: "bird",
    photo: "/assets/products/chicken-broiler-1.jpg",
    fallback: "/assets/products/chicken-broiler.svg",
    images: ["/assets/products/chicken-broiler-2.jpg", "/assets/products/chicken-broiler-3.jpg"],
    specs: [
      { label: "Breed", value: "Farm-raised broiler" },
      { label: "Rearing", value: "Clean farm sheds, quality feed, vet-monitored flock" },
      { label: "Live weight", value: "1.8–2.5 kg per bird" },
      { label: "Preparation", value: "Sold live; cleaned and cut on request at no extra cost" },
      { label: "Bulk orders", value: "Discounts available for functions & events — ask on WhatsApp" },
      { label: "Best for", value: "Everyday curries, fry, biryani and grills" },
    ],
  },
  {
    id: "honey-wild",
    name: "Wild Forest Honey",
    tag: "Raw · Unfiltered",
    desc: "Pure wild honey from Andhra forest blooms. No additives, straight from our hives.",
    pricePerKg: 450,
    mrp: 650,
    category: "honey",
    unit: "kg",
    badge: "Pure",
    photo: unsplash("1558642452-9d2a7deb7f62"),
    fallback: "/assets/products/honey-wild.svg",
    specs: [
      { label: "Type", value: "Raw wild forest honey, multi-floral" },
      { label: "Source", value: "Forest blooms of Andhra Pradesh, harvested from our hives" },
      { label: "Processing", value: "Unheated, unfiltered, unblended — nothing added or removed" },
      { label: "Purity", value: "No added sugar, glucose or preservatives" },
      { label: "Crystallisation", value: "Natural crystallisation may occur — a sign of pure honey" },
      { label: "Storage", value: "Store at room temperature away from sunlight; do not refrigerate" },
    ],
  },
  {
    id: "oil-coconut",
    name: "Cold Pressed Coconut Oil",
    tag: "Wood-pressed · Unrefined",
    desc: "Chekku-pressed from sun-dried copra. Aromatic and unrefined, ideal for cooking and skin.",
    category: "oils",
    badge: "No chemical refining",
    variants: packSizes("coconut", 170, 240),
    photo: "/assets/products/oil-coconut.svg",
    fallback: "/assets/products/oil-coconut.svg",
    specs: [
      { label: "Raw material", value: "Sun-dried copra from coastal Andhra coconuts" },
      { label: "Extraction", value: "Wood-pressed (chekku) at low temperature — no heat, no hexane" },
      { label: "Refining", value: "Unrefined — only settled and cloth-filtered" },
      { label: "Appearance", value: "Clear when warm; solidifies naturally below ~24°C" },
      { label: "Best for", value: "Cooking, frying, hair & skin care, oil pulling" },
      { label: "Shelf life", value: "12 months from pressing; store away from sunlight" },
    ],
  },
  {
    id: "oil-sunflower",
    name: "Cold Pressed Sunflower Oil",
    tag: "Light · Heart-friendly",
    desc: "Cold-pressed sunflower seeds retaining natural vitamin E. Light, neutral, everyday cooking oil.",
    category: "oils",
    variants: packSizes("sunflower", 130, 180),
    photo: "/assets/products/oil-sunflower.svg",
    fallback: "/assets/products/oil-sunflower.svg",
    specs: [
      { label: "Raw material", value: "Select sunflower seeds from Andhra farms" },
      { label: "Extraction", value: "Cold-pressed at low temperature — no heat, no hexane" },
      { label: "Refining", value: "Unrefined — only settled and cloth-filtered" },
      { label: "Nutrition", value: "Naturally retains vitamin E and unsaturated fats" },
      { label: "Best for", value: "Everyday cooking, light frying and salads" },
      { label: "Shelf life", value: "9 months from pressing; store away from sunlight" },
    ],
  },
  {
    id: "oil-groundnut",
    name: "Cold Pressed Groundnut Oil",
    tag: "Chekku-pressed · Unrefined",
    desc:
      "Groundnuts crushed slowly in a wooden chekku, settled and cloth-filtered. No hexane, no chemical refining, no added antioxidants — just the nutty aroma refined oil is processed to remove.",
    category: "oils",
    badge: "Bestseller",
    variants: packSizes("groundnut", 150, 220),
    photo: "/assets/products/oil-groundnut.svg",
    fallback: "/assets/products/oil-groundnut.svg",
    specs: [
      { label: "Raw material", value: "Hand-sorted groundnuts from Andhra farms" },
      { label: "Extraction", value: "Slow wooden chekku pressing — no heat, no hexane" },
      { label: "Refining", value: "Unrefined — settled and cloth-filtered; no added antioxidants" },
      { label: "Aroma", value: "Full nutty aroma that chemical refining strips away" },
      { label: "Best for", value: "Deep frying, curries and traditional Andhra cooking" },
      { label: "Shelf life", value: "12 months from pressing; store away from sunlight" },
    ],
  },
  {
    id: "oil-sesame",
    name: "Cold Pressed Sesame Oil",
    tag: "Nallennai · Traditional",
    desc: "Pure gingelly (til) oil, wood-pressed the traditional way. Deeply flavourful for cooking and oil-pulling.",
    category: "oils",
    variants: packSizes("sesame", 190, 260),
    photo: "/assets/products/oil-sesame.svg",
    fallback: "/assets/products/oil-sesame.svg",
    specs: [
      { label: "Raw material", value: "Premium sesame (til/gingelly) seeds" },
      { label: "Extraction", value: "Traditional wood-pressed (chekku) — no heat, no hexane" },
      { label: "Refining", value: "Unrefined — only settled and cloth-filtered" },
      { label: "Colour & taste", value: "Deep golden, rich traditional nallennai flavour" },
      { label: "Best for", value: "Cooking, pickles, oil pulling and oil baths" },
      { label: "Shelf life", value: "12 months from pressing; store away from sunlight" },
    ],
  },
].map(withDefaultVariant);

export const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "mangoes", label: "Mangoes" },
  { id: "eggs", label: "Eggs" },
  { id: "milk", label: "Milk" },
  { id: "poultry", label: "Chickens" },
  { id: "honey", label: "Honey" },
  { id: "oils", label: "Cold Pressed Oils" },
];

// Sub-menu shown under the "Eggs" tab only — kept to the two egg types the
// farm actually sells (no "Organic"/"Cage-Free" claims without certification
// to back them up).
export const EGG_SUBCATEGORIES = [
  { id: "all", label: "All eggs" },
  { id: "free-range", label: "Free Range" },
  { id: "conventional", label: "Conventional / Pasteurized" },
];

export const HERO_BG = unsplash("1500595046743-cd271d694d30")
  .replace("w=640&h=640", "w=1600&h=1000");

export const CATEGORY_TILES = [
  { id: "mangoes", label: "Mangoes", photo: unsplash("1601493700631-2b16ec4b4716").replace("w=640&h=640", "w=500&h=600") },
  { id: "eggs", label: "Farm Eggs", photo: unsplash("1582722872445-44dc5f7e3c8f").replace("w=640&h=640", "w=500&h=600") },
  { id: "milk", label: "Fresh Milk", photo: unsplash("1550583724-b2692b85b150").replace("w=640&h=640", "w=500&h=600") },
  { id: "poultry", label: "Live Chicken", photo: unsplash("1548550023-2bdb3c5beed7").replace("w=640&h=640", "w=500&h=600") },
  { id: "honey", label: "Wild Honey", photo: unsplash("1558642452-9d2a7deb7f62").replace("w=640&h=640", "w=500&h=600") },
];

export const WHATSAPP_NUMBER = "919704227888"; // Rimmon Farm WhatsApp (91 + 10-digit)

export const CONTACT = {
  email: "info@rimmonfarm.com",
  phoneDisplay: "+91 97042 27888",
  phoneTel: "+919704227888",
  location: "Krishna & Chittoor, Andhra Pradesh",
};

// Footer "Follow us on" row. WhatsApp points at the real farm number; the four
// social handles are not registered yet — swap the "#" for the live profile URL
// as each one goes up. Footer hides any entry still left on "#".
export const SOCIAL = [
  { id: "facebook", label: "Facebook", href: "#" },
  { id: "instagram", label: "Instagram", href: "#" },
  { id: "youtube", label: "YouTube", href: "#" },
  { id: "x", label: "X", href: "#" },
  { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/${WHATSAPP_NUMBER}` },
];

// Small reassurance pills shown in the footer utility bar.
export const FOOTER_BADGES = [
  { id: "fresh", icon: "🌱", label: "100% Farm Fresh" },
  { id: "secure", icon: "🔒", label: "Secure Payments" },
  { id: "delivery", icon: "🚚", label: "Same-day Delivery" },
];

export const formatPrice = (n) => `₹${n}`;
export const unitLabel = (p) => `per ${p.unit}`;

// "50%" style saving shown next to the strike-through MRP; 0 hides the badge.
export const discountPct = (price, mrp) =>
  mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

// ---------------------------------------------------------------------------
// Cart keys
//
// The cart is a flat { key: qty } map. A product without pack sizes keys on its
// own id; one with sizes keys on "<productId>::<variantId>" so 1 L and 5 L of
// the same oil are two independent lines. Old carts saved before pack sizes
// existed still hold bare ids — resolveCartKey() falls back to the default
// variant for those rather than dropping the line.

export const findProduct = (id) => PRODUCTS.find((p) => p.id === id) || null;

// What the catalog pages (Products grid, Home shop section, related products,
// SEO structured data) show — everything except `unlisted` items like the
// join-only launch plan, which only ever reaches the cart via its own CTA.
// Sold-out items (e.g. mangoes out of season) stay in here — their category
// tile/chip and "Out of stock" card still show when browsed to directly —
// callers that want the fully-available view (the "All" tab) filter soldOut
// out themselves.
export const CATALOG_PRODUCTS = PRODUCTS.filter((p) => !p.unlisted);

// Retail value of the 12 free eggs gift — read off the real egg price rather
// than hardcoded, so the welcome banner's savings math can't drift from what
// eggs actually cost on the site.
export const LAUNCH_EGG_VALUE = PRODUCTS.find((p) => p.id === "eggs-free-range")?.pricePerKg ?? 90;

export const defaultVariant = (product) =>
  product?.variants?.find((v) => v.default) || product?.variants?.[0] || null;

export const findVariant = (product, variantId) =>
  product?.variants?.find((v) => v.id === variantId) || defaultVariant(product);

export const cartKey = (productId, variantId) =>
  variantId ? `${productId}::${variantId}` : productId;

/**
 * Expand a cart key into everything the UI and the order payload need.
 * Returns null for a key whose product no longer exists (e.g. a delisted item
 * still sitting in someone's localStorage).
 */
export function resolveCartKey(key) {
  const [productId, variantId] = String(key).split("::");
  const product = findProduct(productId);
  if (!product) return null;
  const variant = product.variants ? findVariant(product, variantId) : null;
  return {
    key,
    product,
    variant,
    name: product.name,
    // "1 L" for the size chip; "1 L pack" on the order line.
    sizeLabel: variant?.label || null,
    unit: variant?.unit || product.unit,
    price: variant?.price ?? product.pricePerKg,
  };
}

/** Turn the cart map into priced order lines, skipping anything unresolvable
 *  or sold out (e.g. a mango still in a cart saved before the season ended). */
export function cartLines(items) {
  return Object.entries(items || {})
    .map(([key, qty]) => {
      const resolved = resolveCartKey(key);
      if (!resolved || qty <= 0 || resolved.product.soldOut) return null;
      return { ...resolved, qty, lineTotal: qty * resolved.price };
    })
    .filter(Boolean);
}
